import React, { useRef } from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import BasicModalLayout from '../../../shared/components/modal/BasicModalLayout';
import { HelpTooltip } from '../../../shared/common/HelpTooltip';
import MaterialIcon from '../../../shared/common/MaterialIcon';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import { log } from '../../../shared/utils/log';
import { PromotionLinkResponse } from '../../../store/api';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';

const StPromotionLinkLine = styled.div`
    display: flex;
    justify-content: left;
    font-size: 14px;
    align-items: center;
`;

const HSpacer = styled.div`
    width: 8px;
`;
const VSpacer = styled.div<{ factor?: number }>`
    height: ${props => (props.factor || 1) * 8}px;
`;

const StQrCodeContainer = styled.div.attrs({ id: 'promotionQRCode' })`
    border: 1px solid lightgrey;
`;

const StInput = styled.input`
    max-width: 95% !important;
    min-width: 95% !important;
    padding: 0 !important;
    width: initial !important;
    border-radius: 0 !important;
    box-shadow: unset !important;
`;

const StClipboardIcon = styled(MaterialIcon)`
    cursor: pointer;
`;
const StHeadline = styled.span`
    color: #9a9a9a;
`;

interface PromotionModalProps {
    onClose: () => void;
    visible: boolean;
    campaignProductId: string;
    promotionData?: PromotionLinkResponse;
    onError?: (error: string) => void;
}

export const PromotionModal = (props: PromotionModalProps) => {
    const { t } = useTranslation();

    function onDownloadQRClick(promotionLinkSvg, campaignProductId) {
        if (promotionLinkSvg) {
            const now = format(new Date(), 'yyyy-mm-dd');
            const fileDownload = require('react-file-download');
            fileDownload(promotionLinkSvg, `CampaignPromotionQR_${campaignProductId}_${now}.svg`);
        }
    }

    let promotionLinkRef = useRef<HTMLInputElement>(null);

    const { promotionData } = props;
    let promotionLinkSvg: string | undefined = undefined;
    let promotionLinkUrl: string | undefined = undefined;
    if (!isEmpty(promotionData)) {
        promotionLinkSvg = promotionData?.promotionLinkSvg;
        promotionLinkUrl = promotionData?.promotionLinkUrl;
    } else if (props.visible) {
        log.error('promotion data is invalid', props as any);
        if (props.onError) {
            props.onError('An error occured while fetching promotion data');
        }
        return null;
    } else {
        return null;
    }
    const header = <h2 style={{ fontSize: '22px' }}>{t('marketing.yourCampaigns.linkAndQr')}</h2>;

    const content = (
        <div>
            <StPromotionLinkLine>
                <StHeadline>{t('marketing.yourCampaigns.promotionLink')}</StHeadline>
                <HSpacer />
                <HelpTooltip text={t('marketing.yourCampaigns.promotionLinkTooltip')} />
            </StPromotionLinkLine>
            <StPromotionLinkLine>
                <StInput readOnly={true} type="text" value={promotionLinkUrl} ref={promotionLinkRef} />
                <StClipboardIcon
                    primary={true}
                    icon={'content_copy'}
                    onClick={() => {
                        promotionLinkRef?.current?.select();
                        void navigator.clipboard.writeText(promotionLinkRef?.current?.value || '');
                    }}
                />
            </StPromotionLinkLine>
            <VSpacer factor={2} />
            <StPromotionLinkLine>
                <StHeadline>{t('marketing.yourCampaigns.qrCode')}</StHeadline>
                <HSpacer />
                <HelpTooltip text={t('marketing.yourCampaigns.promotionQr')} />
            </StPromotionLinkLine>
            <StQrCodeContainer dangerouslySetInnerHTML={{ __html: promotionLinkSvg ?? '' }} />
            <VSpacer />
            <Button
                onClick={() => onDownloadQRClick(promotionLinkSvg, props.campaignProductId)}
                text={t('marketing.yourCampaigns.downloadQrCode')}
            />
        </div>
    );
    return (
        <BasicModalLayout
            header={header}
            content={content}
            cancelButton={
                <Button text={t('close')} onClick={props.onClose} theme={ButtonTheme.NEUTRAL} size={ButtonSize.LARGE} />
            }
            okButton={<div></div>}
            visible={props.visible}
            width={320}
            onClose={props.onClose}
        />
    );
};
